// Dashboard patient search, kept in the URL so it survives leaving /landing.
//
// A search on the dashboard is one picked patient (the suggestion clicked in
// the toolbar's search box) plus the box's "All depts" toggle. It used to
// live only in CareLanding's component state, so anything that remounted the
// page dropped it and the user landed back on the main dashboard: the Orders
// page (/orders/...) returning with navigate("/landing"), a location-switch
// remount, a reload. The search is now mirrored in /landing's query string
//   /landing?q=<patientid>[&qall=1]
// and the dashboard rebuilds the same results from it on mount. Only the
// patient id goes in the URL; the chip's name/DOB are cached for this tab in
// sessionStorage (looked up again when the cache is missing).
//
// Pages opened from a dashboard card (the Orders route) carry the way back in
// their router state (dashboardReturnState) and return through
// returnToDashboard, which restores the query and the scroll position.

import { getPracticeId } from "../config/practice";

export const SEARCH_PARAM = "q";
export const SEARCH_ALL_PARAM = "qall";
const CHIP_PREFIX = "care.dashboardSearch.";
// Per practice: a browser signed into another practice never shows this one's chip.
const chipKey = () => `${CHIP_PREFIX}chip.${getPracticeId() || ""}`;

// { patientid, alldepts } from a location.search string, or null.
export function readDashboardSearch(search) {
  const params = new URLSearchParams(search || "");
  const patientid = (params.get(SEARCH_PARAM) || "").trim();
  if (!patientid) return null;
  return { patientid, alldepts: params.get(SEARCH_ALL_PARAM) === "1" };
}

// Stable identity of a search ("" for none).
export function dashboardSearchKey(value) {
  if (!value || !value.patientid) return "";
  return `${value.patientid}|${value.alldepts ? 1 : 0}`;
}

// location.search with the search set to `value` (null removes it); every
// other parameter is kept. Returns "" or "?...".
export function withDashboardSearch(search, value) {
  const params = new URLSearchParams(search || "");
  params.delete(SEARCH_PARAM);
  params.delete(SEARCH_ALL_PARAM);
  if (value && value.patientid) {
    params.set(SEARCH_PARAM, String(value.patientid));
    if (value.alldepts) params.set(SEARCH_ALL_PARAM, "1");
  }
  const next = params.toString();
  return next ? `?${next}` : "";
}

// location.search without the search parameters (the rest of the query).
export const stripDashboardSearch = (search) => withDashboardSearch(search, null);

// The searched patient's chip ({ patientid, patientinfo, lastname, dob }),
// for this browser tab only. Storage can be unavailable (private mode,
// blocked site data): then the dashboard looks the patient up again.
export function saveSearchChip(chip) {
  try {
    if (!chip || !chip.patientid) {
      window.sessionStorage.removeItem(chipKey());
      return;
    }
    window.sessionStorage.setItem(
      chipKey(),
      JSON.stringify({
        patientid: String(chip.patientid),
        patientinfo: chip.patientinfo || "",
        lastname: chip.lastname || "",
        dob: chip.dob || "",
      }),
    );
  } catch (e) {
    /* no storage: the chip is looked up again on return */
  }
}

export function loadSearchChip(patientid) {
  try {
    const chip = JSON.parse(window.sessionStorage.getItem(chipKey()) || "null");
    if (chip && String(chip.patientid) === String(patientid)) return chip;
  } catch (e) {
    /* unreadable: look the patient up again */
  }
  return null;
}

// Sign-out: drop every cached chip (patient name/DOB) of every practice.
export function clearSearchChips() {
  try {
    const store = window.sessionStorage;
    const keys = [];
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i);
      if (k && k.startsWith(CHIP_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => store.removeItem(k));
  } catch (e) {
    /* no storage: nothing cached */
  }
}

// Router state for a page opened from the dashboard: the exact dashboard
// URL (with its search) and the scroll position to come back to.
export function dashboardReturnState(location) {
  const pathname = (location && location.pathname) || "/landing";
  return {
    returnTo: `${pathname}${(location && location.search) || ""}`,
    scrollY: typeof window !== "undefined" ? window.scrollY || 0 : 0,
  };
}

// The way back from such a page. With a returnTo it goes to that dashboard
// URL (same search, scroll restored); otherwise as before: the patient's
// search when the page was opened from a filtered view (filter !== "all"),
// else the plain dashboard.
export function returnToDashboard(navigate, location, { patientid, filter } = {}) {
  const st = (location && location.state) || {};
  if (typeof st.returnTo === "string" && st.returnTo.startsWith("/landing")) {
    navigate(st.returnTo, {
      state: typeof st.scrollY === "number" ? { restoreScrollY: st.scrollY } : null,
    });
    return;
  }
  if (filter && filter !== "all" && patientid) {
    navigate("/landing", { state: { fromPatientId: patientid } });
    return;
  }
  navigate("/landing");
}

// A dashboard card's "Orders" action: the Orders page, carrying the way back
// (this dashboard URL with its search, and the scroll position). The route's
// last segment keeps its old meaning ("filter" when opened from a patient
// search, which the Orders page's legacy return reads).
export function openOrdersFromDashboard(navigate, location, data, searchTerm) {
  const filtered = Boolean(
    searchTerm ||
      (location && location.state && location.state.fromPatientId) ||
      readDashboardSearch(location && location.search),
  );
  navigate(`/orders/${data.appointmentid}/${data.startdate}/${filtered ? "filter" : "all"}`, {
    state: dashboardReturnState(location),
  });
}
